<template>
  <v-dialog v-model="dialog" scrollable max-width="1100">
    <v-card v-if="dialog" min-height="400">
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px">
        {{ $t('charging-point-configuration.title') }}
        <!-- Comment elle écrit ses booléens : une valeur appliquée en masse part dans cette écriture -->
        <v-tooltip v-if="data && data.booleanSpelling" bottom>
          <template #activator="{on}">
            <v-chip x-small outlined class="ml-2 mono" v-on="on">{{ data.booleanSpelling }} / {{ falseOf(data.booleanSpelling) }}</v-chip>
          </template>
          {{ $t('charging-point-configuration.boolean-spelling') }}
        </v-tooltip>
        <v-spacer />
        <span class="text-caption" :class="readFailed ? 'orange--text' : 'text--secondary'">
          <template v-if="reading">{{ $t('charging-point-configuration.reading') }}</template>
          <template v-else-if="readFailed">{{ data && data.readAt ? $t('charging-point-configuration.read-failed', {date: formatDate(data.readAt, 'DD/MM/YYYY HH:mm')}) : $t('charging-point-configuration.read-failed-never') }}</template>
          <template v-else-if="data && data.readAt">{{ $t('charging-point-configuration.read-at', {date: formatDate(data.readAt, 'DD/MM/YYYY HH:mm')}) }}</template>
          <template v-else-if="data">{{ $t('charging-point-configuration.never-read') }}</template>
        </span>
        <v-btn small outlined color="primary" :loading="reading" @click="read">
          <v-icon small left>mdi-refresh</v-icon>{{ $t('charging-point-configuration.read') }}
        </v-btn>
      </v-card-title>
      <v-tabs v-model="tab" class="flex-grow-0">
        <v-tab>{{ $t('charging-point-configuration.keys') }}</v-tab>
        <v-tab>{{ $t('charging-point-configuration.history') }}<span v-if="data && events.length" class="ml-1">({{ events.length }})</span></v-tab>
      </v-tabs>
      <v-progress-linear :active="reading || pendingKeys.length > 0" indeterminate height="2" color="primary" />
      <v-divider v-if="!(reading || pendingKeys.length)" />

      <v-card-text class="pt-4">
        <div v-if="!data" class="text-center my-6"><v-progress-circular indeterminate color="primary" /></div>

        <template v-else-if="tab === 0">
          <!-- Ce que la borne remonte (température, signal…), sous notre nom : sa dernière lecture ; « Lire » la rafraîchit -->
          <div v-if="data.informations && data.informations.length" class="mb-4">
            <div class="text-caption text--secondary mb-1">
              {{ $t('charging-point-configuration.informations') }}<template v-if="data.readAt"> · {{ $t('charging-point-configuration.read-at', {date: formatDate(data.readAt, 'DD/MM/YYYY HH:mm')}) }}</template>
            </div>
            <div class="d-flex flex-wrap" style="gap: 8px">
              <v-tooltip v-for="information in data.informations" :key="information.code" bottom>
                <template #activator="{on}">
                  <v-card outlined class="information px-3 py-2" v-on="on">
                    <div class="text-caption text--secondary text-truncate">{{ information.label || information.code }}</div>
                    <div class="text-subtitle-1 font-weight-medium mono text-truncate">
                      {{ information.value === null || information.value === '' ? '—' : information.value }}<span v-if="information.unit && information.value" class="text-caption text--secondary ml-1">{{ information.unit }}</span>
                    </div>
                  </v-card>
                </template>
                <span class="mono">{{ information.key }}</span>
              </v-tooltip>
            </div>
          </div>

          <!-- Les clés suivies en écart : pas la valeur attendue, ou absentes -->
          <v-alert v-if="data.deviations && data.deviations.length" type="warning" text dense>
            <div class="font-weight-medium">{{ $tc('charging-point-configuration.deviations', data.deviations.length, {count: data.deviations.length}) }}</div>
            <div v-for="deviation in data.deviations" :key="deviation.code" class="mono text-caption">
              {{ deviation.key }} : {{ deviation.present ? display(deviation.value) : $t('charging-point-configuration.absent') }} → {{ deviation.expected }}
            </div>
          </v-alert>
          <v-alert v-if="!data.keys.length && !reading" type="info" text dense>{{ $t('charging-point-configuration.empty') }}</v-alert>
          <template v-else>
            <v-text-field v-model="search" :label="$t('charging-point-configuration.search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable class="mb-2" />
            <!-- Largeurs fixes : une valeur longue passe à la ligne au lieu d'élargir le tableau -->
            <v-simple-table dense class="keys-table">
              <colgroup>
                <col style="width: 34%">
                <col style="width: 40%">
                <col style="width: 26%">
              </colgroup>
              <thead>
                <tr>
                  <th>{{ $t('charging-point.configuration-key') }}</th>
                  <th>{{ $t('charging-point.configuration-value') }}</th>
                  <th>{{ $t('charging-point-configuration.last-change') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in filteredKeys" :key="item.key">
                  <td class="mono">
                    <component :is="item.readonly ? 'i' : 'b'" :class="{changed: isEdited(item)}">{{ item.key }}</component>
                    <div v-if="item.trackedAs && item.trackedAs.toLowerCase() !== item.key.toLowerCase()" class="text-caption text--secondary">{{ $t('charging-point-configuration.tracked-as', {code: item.trackedAs}) }}</div>
                    <div v-if="item.readonly" class="text-caption text--secondary">{{ $t('charging-point-configuration.readonly') }}</div>
                  </td>
                  <td>
                    <span v-if="item.readonly" class="mono">{{ item.sensitive ? $t('charging-point-configuration.hidden') : item.value }}</span>
                    <!-- Un booléen : un interrupteur ; la valeur part dans l'écriture de la borne (True, 1…) -->
                    <v-switch
                        v-else-if="item.boolean"
                        :input-value="booleanOf(edits[item.key])"
                        :label="edits[item.key]"
                        dense
                        hide-details
                        inset
                        class="mt-0 pt-0 mono"
                        @change="value => edits[item.key] = writeBoolean(value, item.value)"
                    />
                    <v-text-field v-else v-model="edits[item.key]" dense hide-details :placeholder="item.sensitive ? $t('charging-point-configuration.hidden-placeholder') : ''" class="mono mt-0 pt-0" />
                    <div v-if="item.expected !== null && item.expected !== undefined" class="text-caption mono" :class="item.deviates ? 'orange--text' : 'text--secondary'">{{ $t('charging-point-configuration.expected', {value: item.expected}) }}</div>
                  </td>
                  <!-- La demande en cours, ou la réponse de la borne plus récente que le dernier changement constaté, sinon ce changement -->
                  <td class="text-caption">
                    <span v-if="pending[item.key]" class="text--secondary"><v-progress-circular indeterminate size="12" width="2" class="mr-1" />{{ $t('charging-point-configuration.pending') }}</span>
                    <a v-else-if="answerOf(item)" href="#" @click.prevent="history(item.key)">
                      <v-chip x-small :color="ANSWER_COLORS[answerOf(item).status] || 'grey'" text-color="white" class="mr-1">{{ $t('charging-point-configuration.answer-statuses.' + answerOf(item).status) }}</v-chip>{{ formatDate(answerOf(item).at, 'DD/MM HH:mm') }}
                    </a>
                    <a v-else-if="item.lastChange" href="#" @click.prevent="history(item.key)">
                      <v-chip x-small :color="ORIGIN_COLORS[item.lastChange.origin]" text-color="white" class="mr-1">{{ $t('charging-point-configuration.origins.' + item.lastChange.origin) }}</v-chip>{{ formatDate(item.lastChange.observedAt, 'DD/MM/YYYY') }}
                    </a>
                    <span v-else-if="item.since" class="text--secondary">{{ $t('charging-point-configuration.since', {date: formatDate(item.since, 'DD/MM/YYYY')}) }}</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <!-- Une clé que la borne ne rapporte pas (clé d'écriture seule, ex. clé de signature d'un firmware) -->
          <div class="d-flex align-center flex-wrap mt-4" style="gap: 8px">
            <v-text-field v-model="extra.key" :label="$t('charging-point-configuration.other-key')" outlined dense hide-details style="max-width: 320px" class="mono" />
            <v-text-field v-model="extra.value" :label="$t('charging-point.configuration-value')" outlined dense hide-details style="max-width: 320px" class="mono" />
          </div>
        </template>

        <template v-else>
          <div v-if="historyKey" class="mb-2">
            <v-chip small close @click:close="history(null)"><span class="mono">{{ historyKey }}</span></v-chip>
          </div>
          <div v-if="!shownEvents.length" class="text--secondary">{{ $t('charging-point-configuration.no-change') }}</div>
          <v-simple-table v-else dense>
            <tbody>
              <tr v-for="change in shownEvents" :key="change.id">
                <td class="text-caption text-no-wrap" style="width: 1%">{{ formatDate(change.observedAt, 'DD/MM/YYYY HH:mm') }}</td>
                <td class="mono"><a href="#" @click.prevent="history(change.key)">{{ change.key }}</a></td>
                <!-- Une réponse de la borne à une de nos demandes (refusée, à redémarrer…) -->
                <td v-if="change.answer" class="text-caption">
                  <v-chip x-small :color="ANSWER_COLORS[change.answer] || 'grey'" text-color="white" class="mr-1">{{ $t('charging-point-configuration.answer-statuses.' + change.answer) }}</v-chip>{{ $t('charging-point-configuration.answer-to-request') }}
                </td>
                <td v-else class="mono">
                  <template v-if="change.sensitive">{{ $t('charging-point-configuration.secret-changed') }}</template>
                  <template v-else>
                    <span class="text--secondary">{{ change.appeared ? $t('charging-point-configuration.absent') : display(change.before) }}</span> → {{ change.disappeared ? $t('charging-point-configuration.absent') : display(change.after) }}
                  </template>
                </td>
                <td class="text-right text-no-wrap">
                  <v-chip v-if="!change.answer" x-small :color="ORIGIN_COLORS[change.origin]" text-color="white">{{ $t('charging-point-configuration.origins.' + change.origin) }}</v-chip>
                  <div v-if="change.byName || (change.origin === 'firmware' && change.firmwareVersion)" class="text-caption text--secondary">{{ change.byName || change.firmwareVersion }}</div>
                </td>
                <td class="text-right" style="width: 1%">
                  <v-btn v-if="!change.answer && restorable(change)" x-small text color="primary" :loading="saving" @click="restore(change)">{{ $t('charging-point-configuration.restore') }}</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span v-if="pendingKeys.length" class="text-caption">{{ $tc('charging-point-configuration.pending-count', pendingKeys.length, {count: pendingKeys.length}) }}</span>
        <span v-else-if="editedKeys.length" class="text-caption">{{ $tc('charging-point-configuration.edited', editedKeys.length, {count: editedKeys.length}) }}</span>
        <v-spacer />
        <v-btn small text @click="close">{{ $t('close') }}</v-btn>
        <v-btn small color="primary" :disabled="!editedKeys.length || pendingKeys.length > 0" :loading="saving" @click="save">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CommandRepository from "@repository/CommandRepository";
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import ChargingPointConfigurationValue from "@pages/ChargingPointConfiguration/ChargingPointConfigurationValue";

// Attente d'une réponse de la borne : un essai toutes les 3 s, une minute au plus.
const POLL_MS = 3000
const POLL_TRIES = 20

/**
 * La configuration OCPP d'une borne : la dernière lecture s'affiche tout de suite et la borne est relue à chaque
 * ouverture (la photo peut dater). Son historique clé par clé mêle les changements constatés (d'où ils viennent) et les
 * réponses de la borne à nos demandes. On modifie une clé ici : la ligne attend la réponse de la borne, puis la montre ;
 * le reste de la fenêtre ne bouge pas. Une clé sensible ne se montre jamais : on peut seulement la remplacer.
 */
export default {
  props: {
    chargingPoint: Object,
  },

  data() {
    return {
      dialog: false,
      data: null,
      tab: 0,
      search: '',
      edits: {},
      extra: {key: '', value: ''},
      reading: false,
      readFailed: false,
      saving: false,
      // Les clés envoyées qui attendent la réponse de la borne : clé → moment de l'envoi (ms).
      pending: {},
      historyKey: null,
      keyChanges: null,
      readTimer: null,
      sendTimer: null,
      ANSWER_COLORS: {Accepted: 'success', RebootRequired: 'teal', Rejected: 'error', NotSupported: 'deep-orange'},
      ORIGIN_COLORS: {ours: 'primary', firmware: 'purple', external: 'orange'},
    }
  },

  computed: {
    pendingKeys() {
      return Object.keys(this.pending)
    },
    // Les changements constatés et les réponses de la borne, du plus récent au plus ancien.
    events() {
      if (!this.data) {
        return []
      }
      const answers = (this.data.answers || []).map((answer, index) => ({id: 'answer-' + index, key: answer.key, observedAt: answer.at, answer: answer.status, byName: answer.byName}))
      return [...this.data.changes, ...answers].sort((a, b) => new Date(b.observedAt) - new Date(a.observedAt))
    },
    shownEvents() {
      if (!this.historyKey) {
        return this.events
      }
      const answers = this.events.filter(event => event.answer && event.key === this.historyKey)
      return [...(this.keyChanges || []), ...answers].sort((a, b) => new Date(b.observedAt) - new Date(a.observedAt))
    },
    filteredKeys() {
      const search = (this.search || '').toLowerCase()
      return this.data.keys.filter(item => !search || item.key.toLowerCase().includes(search) || (item.value || '').toLowerCase().includes(search))
    },
    editedKeys() {
      const edited = this.data ? this.data.keys.filter(item => this.isEdited(item)).map(item => ({key: item.key, value: this.edits[item.key]})) : []
      if (this.extra.key.trim()) {
        edited.push({key: this.extra.key.trim(), value: this.extra.value})
      }
      return edited
    },
  },

  beforeDestroy() {
    this.stopTimers()
  },

  methods: {
    booleanOf(value) {
      return ChargingPointConfigurationValue.truth(value) === true
    },
    // Vrai ou faux, écrit comme la borne écrit cette clé (sinon comme ses autres booléens).
    writeBoolean(truth, current) {
      return ChargingPointConfigurationValue.inFormatOf(truth ? 'true' : 'false', current, this.data.booleanSpelling)
    },
    // L'écriture de « faux » qui va avec (True → False, 1 → 0, YES → NO).
    falseOf(spelling) {
      const pairs = {true: 'false', '1': '0', yes: 'no', on: 'off', enabled: 'disabled'}
      const word = pairs[spelling.toLowerCase()] || ''
      if (spelling === spelling.toUpperCase() && spelling !== spelling.toLowerCase()) {
        return word.toUpperCase()
      }
      return spelling[0] !== spelling[0].toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word
    },

    open() {
      this.dialog = true
      this.tab = 0
      this.data = null
      this.historyKey = null
      this.pending = {}
      this.readFailed = false
      // La dernière lecture d'abord, puis la borne est relue : ce qu'on voit est sûr.
      this.load().then(() => this.read())
    },

    close() {
      this.dialog = false
      this.stopTimers()
    },

    stopTimers() {
      clearTimeout(this.readTimer)
      clearTimeout(this.sendTimer)
      this.reading = false
    },

    load() {
      return ChargingPointConfigurationRepository.ofChargingPoint(this.chargingPoint.id).then(data => {
        this.apply(data)
      }).catch(err => {
        this.notifyError(err)
      })
    },

    // Une nouvelle photo, sans perdre ce qui est saisi : une clé modifiée garde sa saisie (envoyée, jusqu'à la réponse ;
    // refusée, pour la corriger).
    apply(data) {
      const kept = this.data ? Object.fromEntries(this.data.keys.filter(item => this.isEdited(item)).map(item => [item.key, this.edits[item.key]])) : {}
      this.data = data
      this.edits = Object.fromEntries(data.keys.filter(item => !item.readonly).map(item => [item.key, item.key in kept ? kept[item.key] : (item.sensitive ? '' : item.value)]))
    },

    // Demander la configuration à la borne, puis attendre que la date de lecture change (une minute au plus).
    read() {
      if (!this.dialog) {
        return
      }
      const before = this.data && this.data.readAt
      this.reading = true
      this.readFailed = false
      ChargingPointConfigurationRepository.read(this.chargingPoint.id).then(() => {
        this.poll('readTimer', data => data.readAt !== before, POLL_TRIES, done => {
          this.reading = false
          this.readFailed = !done
        })
      }).catch(() => {
        // Borne hors ligne, par exemple : on garde la dernière lecture et on le dit.
        this.reading = false
        this.readFailed = true
      })
    },

    // Relire la photo toutes les 3 s jusqu'à ce que `done` soit vrai ou que les essais soient épuisés ; chaque photo
    // reçue s'affiche (les réponses arrivent une à une), puis `finish(réussi)`.
    poll(timer, done, tries, finish) {
      clearTimeout(this[timer])
      this[timer] = setTimeout(() => {
        ChargingPointConfigurationRepository.ofChargingPoint(this.chargingPoint.id).then(data => {
          if (!this.dialog) {
            return
          }
          this.apply(data)
          if (done(data)) {
            finish(true)
          } else if (tries <= 1) {
            finish(false)
          } else {
            this.poll(timer, done, tries - 1, finish)
          }
        }).catch(err => {
          this.notifyError(err)
          finish(false)
        })
      }, POLL_MS)
    },

    // La réponse de la borne à une clé, si elle est plus récente que le dernier changement constaté (sinon le changement dit tout).
    answerOf(item) {
      const answer = (this.data.answers || []).find(answer => answer.key.toLowerCase() === item.key.toLowerCase())
      if (!answer) {
        return null
      }
      return !item.lastChange || new Date(answer.at) > new Date(item.lastChange.observedAt) ? answer : null
    },

    // Les clés envoyées attendent chacune leur réponse ; à la fin, ce qui n'a pas répondu est dit.
    send(changed) {
      // Un booléen part dans l'écriture de la borne : celle de la clé, sinon celle de ses autres booléens (« true » tapé
      // sur une Alfen part en « True », sur une clé en 0/1 en « 1 »).
      changed = changed.map(change => {
        const current = (this.data.keys.find(item => item.key.toLowerCase() === change.key.toLowerCase()) || {}).value
        return {...change, value: ChargingPointConfigurationValue.inFormatOf(change.value, current, this.data.booleanSpelling)}
      })
      this.saving = true
      return CommandRepository.changeConfigurations(this.chargingPoint.id, changed).then(() => {
        // Une marge pour l'horloge du serveur : une réponse un peu antérieure à l'envoi compte.
        const sentAt = Date.now() - 60000
        this.pending = {...this.pending, ...Object.fromEntries(changed.map(change => [change.key, sentAt]))}
        // Une clé est réglée quand la borne a répondu et, si elle a accepté, quand elle a été relue depuis (la valeur affichée est la vraie).
        const settled = (data, key) => {
          const answer = (data.answers || []).find(answer => answer.key.toLowerCase() === key.toLowerCase() && new Date(answer.at).getTime() >= this.pending[key])
          return !!answer && (!['Accepted', 'RebootRequired'].includes(answer.status) || (!!data.readAt && new Date(data.readAt) >= new Date(answer.at)))
        }
        const settle = data => {
          const done = this.pendingKeys.filter(key => settled(data, key))
          // Une clé masquée ne garde pas la saisie envoyée.
          data.keys.filter(item => item.sensitive && done.includes(item.key)).forEach(item => {
            this.edits[item.key] = ''
          })
          this.pending = Object.fromEntries(Object.entries(this.pending).filter(([key]) => !done.includes(key)))
          return !this.pendingKeys.length
        }
        this.poll('sendTimer', settle, POLL_TRIES, done => {
          if (!done) {
            this.$dialog.notify.warning(this.$t('charging-point-configuration.no-answer'))
            this.pending = {}
          }
        })
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.saving = false
      })
    },

    isEdited(item) {
      if (item.readonly || !(item.key in this.edits)) {
        return false
      }
      return item.sensitive ? this.edits[item.key] !== '' : this.edits[item.key] !== item.value
    },

    save() {
      const changed = this.editedKeys
      this.$dialog.confirm({text: this.$t('charging-point.change-configuration-confirmation')}).then(result => {
        if (!result) {
          return
        }
        this.send(changed).then(() => {
          this.extra = {key: '', value: ''}
        })
      })
    },

    // Remettre la valeur d'avant un changement : une clé modifiable, ni secrète, ni apparue ou disparue depuis.
    restorable(change) {
      const entry = this.data.keys.find(item => item.key === change.key)
      return entry && !entry.readonly && !change.sensitive && !change.appeared && !change.disappeared
    },

    restore(change) {
      this.$dialog.confirm({text: this.$t('charging-point-configuration.restore-confirm', {key: change.key, value: this.display(change.before)})}).then(result => {
        if (!result) {
          return
        }
        this.tab = 0
        this.send([{key: change.key, value: change.before ?? ''}])
      })
    },

    history(key) {
      this.historyKey = key
      this.tab = 1
      this.keyChanges = null
      if (key) {
        ChargingPointConfigurationRepository.ofChargingPoint(this.chargingPoint.id, key).then(data => {
          this.keyChanges = data.changes
        }).catch(err => {
          this.notifyError(err)
        })
      }
    },

    display(value) {
      return value === null || value === '' ? '∅' : value
    },
  },
}
</script>

<style scoped lang="scss">
.information {
  min-width: 140px;
  max-width: 240px;
}
.mono {
  font-family: monospace;
}
.changed {
  color: var(--v-error-base);
}
.keys-table ::v-deep table {
  table-layout: fixed;
}
.keys-table td {
  overflow-wrap: anywhere;
}
</style>
